import React, { Fragment, useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import Form from './Form';
import FrontCanvas from './FrontCanvas';
import BackCanvas from './BackCanvas';
import ConfirmationModal from './ConfirmationModal';
import './Editor.scss';
import { Helmet } from 'react-helmet';
import Layout from './../Layout';
import * as selectors from './Editor.selectors';
import * as actions from './Editor.actions';
import { stringifyEqualityCheck, showNotification } from './../../utils';
import * as loginSelectors from './../Login/Login.selectors';
import { Link } from 'react-router-dom';
import * as dialog from './../common/dialog';
import { changeFrontAll, changeBackAll, changePrevFrontAll, changePrevBackAll, changePrevLogoAll, setTemplateCodeCanvas, setUser } from './CanvasActions';

const Editor = (props) => {
    /* State to props */
    const templateCode = useSelector(selectors.getTemplateCode);
    const name = useSelector(selectors.getName);
    const credentials = useSelector(selectors.getCredentials);
    const title1 = useSelector(selectors.getTitle1);
    const title2 = useSelector(selectors.getTitle2);
    const title3 = useSelector(selectors.getTitle3);
    const department1 = useSelector(selectors.getDepartment1);
    const department2 = useSelector(selectors.getDepartment2);
    const department3 = useSelector(selectors.getDepartment3);
    const phone = useSelector(selectors.getPhone);
    const clinic = useSelector(selectors.getClinic);
    const fax = useSelector(selectors.getFax);
    const pager = useSelector(selectors.getPager);
    const address = useSelector(selectors.getAddress);
    const address2 = useSelector(selectors.getAddress2);
    const address3 = useSelector(selectors.getAddress3);
    const city = useSelector(selectors.getCity);
    const state = useSelector(selectors.getState);
    const zip = useSelector(selectors.getZip);
    const email = useSelector(selectors.getEmail);
    const showEmail = useSelector(selectors.getShowEmail);
    const open1 = useSelector(selectors.getOpen1);
    const open2 = useSelector(selectors.getOpen2);
    const open3 = useSelector(selectors.getOpen3);
    const quantity = useSelector(selectors.getQuantity);
    const cell = useSelector(selectors.getCell);
    const showCell = useSelector(selectors.getShowCell);
    const locationNames = useSelector(selectors.getLocationNames, stringifyEqualityCheck);
    const locationPhones = useSelector(selectors.getLocationPhones, stringifyEqualityCheck);
    const locationFaxes = useSelector(selectors.getLocationFaxes, stringifyEqualityCheck);
    const locationNursePhones = useSelector(selectors.getLocationNursePhones, stringifyEqualityCheck);
    const locationAddresses1 = useSelector(selectors.getLocationAddresses1, stringifyEqualityCheck);
    const locationAddresses2 = useSelector(selectors.getLocationAddresses2, stringifyEqualityCheck);
    const locationAddresses3 = useSelector(selectors.getLocationAddresses3, stringifyEqualityCheck);
    const locationSuites = useSelector(selectors.getLocationSuites, stringifyEqualityCheck);
    const locationCities = useSelector(selectors.getLocationCities, stringifyEqualityCheck);
    const locationStates = useSelector(selectors.getLocationStates, stringifyEqualityCheck);
    const locationZips = useSelector(selectors.getLocationZips, stringifyEqualityCheck);
    const color = useSelector(selectors.getColor);
    const stock = useSelector(selectors.getStock);
    const titles = useSelector(selectors.getTitles, stringifyEqualityCheck);
    const notes = useSelector(selectors.getNotes);
    const front = useSelector(selectors.getFront);
    const back = useSelector(selectors.getBack);
    const suiteRoom = useSelector(selectors.getSuiteRoom);
    const firstName = useSelector(selectors.getFirstName);
    const middleName = useSelector(selectors.getMiddleName);
    const lastName = useSelector(selectors.getLastName);
    const primaryTitle = useSelector(selectors.getPrimaryTitle);
    const secondaryTitle = useSelector(selectors.getSecondaryTitle);
    const title4 = useSelector(selectors.getTitle4);
    const main = useSelector(selectors.getMain);
    const direct = useSelector(selectors.getDirect);
    const website = useSelector(selectors.getWebsite);
    const schedulingCard = useSelector(selectors.getSchedulingCard);
    const contact1Phone = useSelector(selectors.getContact1Phone);
    const contact1Type = useSelector(selectors.getContact1Type);
    const contact2Phone = useSelector(selectors.getContact2Phone);
    const contact2Type = useSelector(selectors.getContact2Type);
    const locations = useSelector(selectors.getLocations);
    const personsNames = useSelector(selectors.getPersonsNames, stringifyEqualityCheck);
    const personsTitles = useSelector(selectors.getPersonsTitles, stringifyEqualityCheck);
    const addingToCart = useSelector(selectors.getAddingToCart);
    const isFavorite = useSelector(selectors.getIsFavorite);
    const checkingFavorite = useSelector(selectors.getCheckingFavorite);
    const savingWork = useSelector(selectors.getSavingWork);
    const action = useSelector(selectors.getAction);
    const quantity1 = useSelector(selectors.getQuantity1);
    const quantity2 = useSelector(selectors.getQuantity2);
    const quantity3 = useSelector(selectors.getQuantity3);
    const addToCartOrderType = useSelector(selectors.getAddToCartOrderType);
    const addToCartType = useSelector(selectors.getAddToCartType);
    const password = useSelector(selectors.getPassword);
    const showChecks = useSelector(selectors.getShowChecks);
    const fetchingCheckControl = useSelector(selectors.getFetchingCheckControl);
    const checkControl = useSelector(selectors.getCheckControl);
    const addEnvelopes = useSelector(selectors.getAddEnvelopes);
    const window = useSelector(selectors.getWindow);
    const phoneLabel1 = useSelector(selectors.getPhoneLabel1);
    const phoneLabel2 = useSelector(selectors.getPhoneLabel2);
    const phoneLabel3 = useSelector(selectors.getPhoneLabel3);
    const phoneOtherLabel1 = useSelector(selectors.getPhoneOtherLabel1);
    const phoneOtherLabel2 = useSelector(selectors.getPhoneOtherLabel2);
    const phoneOtherLabel3 = useSelector(selectors.getPhoneOtherLabel3);
    const showSocialMediaIcons = useSelector(selectors.getShowSocialMediaIcons);
    const letterheadPersons = useSelector(selectors.getLetterheadPersons, stringifyEqualityCheck);
    const prescriptionPadDoctors = useSelector(selectors.getPrescriptionPadDoctors, stringifyEqualityCheck);
    const additionalField = useSelector(selectors.getAdditionalField);
    const language = useSelector(selectors.getLanguage);
    const clinicId = useSelector(selectors.getClinicId);
    const locationId = useSelector(selectors.getLocationId);
    const nursePhone = useSelector(selectors.getNursePhone);
    const fontType = useSelector(selectors.getFontType);
    const fetchingClinics = useSelector(selectors.getFetchingClinics);
    const fetchingClinicLocations = useSelector(selectors.getFetchingClinicLocations);
    const clinics = useSelector(selectors.getClinics, stringifyEqualityCheck);
    const clinicLocations = useSelector(selectors.getClinicLocations, stringifyEqualityCheck);
    const availableItems = useSelector(selectors.getAvailableItems, stringifyEqualityCheck);
    const fetchingAvailableItems = useSelector(selectors.getFetchingAvailableItems, stringifyEqualityCheck);
    const cxmlData = useSelector(loginSelectors.getCxmlData, stringifyEqualityCheck);
    const user = useSelector(loginSelectors.getUser, stringifyEqualityCheck);
    const dialogState = useSelector(dialog.selectors.getDialogState, stringifyEqualityCheck);

    /* Dispatch to props */
    const dispatch = useDispatch();
    const setTemplateCode = useCallback((value) => dispatch(actions.setTemplateCode(value)), [dispatch]);
    const setName = useCallback((value) => dispatch(actions.setName(value)), [dispatch]);
    const setCredentials = useCallback((value) => dispatch(actions.setCredentials(value)), [dispatch]);
    const setTitle1 = useCallback((value) => dispatch(actions.setTitle1(value)), [dispatch]);
    const setTitle2 = useCallback((value) => dispatch(actions.setTitle2(value)), [dispatch]);
    const setTitle3 = useCallback((value) => dispatch(actions.setTitle3(value)), [dispatch]);
    const setDepartment1 = useCallback((value) => dispatch(actions.setDepartment1(value)), [dispatch]);
    const setDepartment2 = useCallback((value) => dispatch(actions.setDepartment2(value)), [dispatch]);
    const setDepartment3 = useCallback((value) => dispatch(actions.setDepartment3(value)), [dispatch]);
    const setPhone = useCallback((value) => dispatch(actions.setPhone(value)), [dispatch]);
    const setClinic = useCallback((value) => dispatch(actions.setClinic(value)), [dispatch]);
    const setFax = useCallback((value) => dispatch(actions.setFax(value)), [dispatch]);
    const setPager = useCallback((value) => dispatch(actions.setPager(value)), [dispatch]);
    const setNursePhone = useCallback((value) => dispatch(actions.setNursePhone(value)), [dispatch]);
    const setFontType = useCallback((value) => dispatch(actions.setFontType(value)), [dispatch]);
    const setAddress = useCallback((value) => dispatch(actions.setAddress(value)), [dispatch]);
    const setAddress2 = useCallback((value) => dispatch(actions.setAddress2(value)), [dispatch]);
    const setAddress3 = useCallback((value) => dispatch(actions.setAddress3(value)), [dispatch]);
    const setCity = useCallback((value) => dispatch(actions.setCity(value)), [dispatch]);
    const setState = useCallback((value) => dispatch(actions.setState(value)), [dispatch]);
    const setZip = useCallback((value) => dispatch(actions.setZip(value)), [dispatch]);
    const setEmail = useCallback((value) => dispatch(actions.setEmail(value)), [dispatch]);
    const setShowEmail = useCallback((value) => dispatch(actions.setShowEmail(value)), [dispatch]);
    const setOpen1 = useCallback((value) => dispatch(actions.setOpen1(value)), [dispatch]);
    const setOpen2 = useCallback((value) => dispatch(actions.setOpen2(value)), [dispatch]);
    const setOpen3 = useCallback((value) => dispatch(actions.setOpen3(value)), [dispatch]);
    const setQuantity = useCallback((value) => dispatch(actions.setQuantity(value)), [dispatch]);
    const setFrontDataUrl = useCallback((value) => dispatch(actions.setFrontDataUrl(value)), [dispatch]);
    const setBackDataUrl = useCallback((value) => dispatch(actions.setBackDataUrl(value)), [dispatch]);
    const setCell = useCallback((value) => dispatch(actions.setCell(value)), [dispatch]);
    const setShowCell = useCallback((value) => dispatch(actions.setShowCell(value)), [dispatch]);
    const setLocationNames = useCallback((value) => dispatch(actions.setLocationNames(value)), [dispatch]);
    const setLocationPhones = useCallback((value) => dispatch(actions.setLocationPhones(value)), [dispatch]);
    const setLocationFaxes = useCallback((value) => dispatch(actions.setLocationFaxes(value)), [dispatch]);
    const setLocationNursePhones = useCallback((value) => dispatch(actions.setLocationNursePhones(value)), [dispatch]);
    const setLocationAddresses1 = useCallback((value) => dispatch(actions.setLocationAddresses1(value)), [dispatch]);
    const setLocationAddresses2 = useCallback((value) => dispatch(actions.setLocationAddresses2(value)), [dispatch]);
    const setLocationAddresses3 = useCallback((value) => dispatch(actions.setLocationAddresses3(value)), [dispatch]);
    const setLocationSuites = useCallback((value) => dispatch(actions.setLocationSuites(value)), [dispatch]);
    const setLocationCities = useCallback((value) => dispatch(actions.setLocationCities(value)), [dispatch]);
    const setLocationStates = useCallback((value) => dispatch(actions.setLocationStates(value)), [dispatch]);
    const setLocationZips = useCallback((value) => dispatch(actions.setLocationZips(value)), [dispatch]);
    const setColor = useCallback((value) => dispatch(actions.setColor(value)), [dispatch]);
    const setStock = useCallback((value) => dispatch(actions.setStock(value)), [dispatch]);
    const setTitles = useCallback((value) => dispatch(actions.setTitles(value)), [dispatch]);
    const setNotes = useCallback((value) => dispatch(actions.setNotes(value)), [dispatch]);
    const setContact1Phone = useCallback((value) => dispatch(actions.setContact1Phone(value)), [dispatch]);
    const setContact1Type = useCallback((value) => dispatch(actions.setContact1Type(value)), [dispatch]);
    const setContact2Phone = useCallback((value) => dispatch(actions.setContact2Phone(value)), [dispatch]);
    const setContact2Type = useCallback((value) => dispatch(actions.setContact2Type(value)), [dispatch]);
    const setQuantity1 = useCallback((value) => dispatch(actions.setQuantity1(value)), [dispatch]);
    const setQuantity2 = useCallback((value) => dispatch(actions.setQuantity2(value)), [dispatch]);
    const setQuantity3 = useCallback((value) => dispatch(actions.setQuantity3(value)), [dispatch]);
    const setLocations = useCallback((value) => dispatch(actions.setLocations(value)), [dispatch]);
    const setFront = useCallback((value) => dispatch(actions.setFront(value)), [dispatch]);
    const setBack = useCallback((value) => dispatch(actions.setBack(value)), [dispatch]);
    const setSuiteRoom = useCallback((value) => dispatch(actions.setSuiteRoom(value)), [dispatch]);
    const setFirstName = useCallback((value) => dispatch(actions.setFirstName(value)), [dispatch]);
    const setMiddleName = useCallback((value) => dispatch(actions.setMiddleName(value)), [dispatch]);
    const setLastName = useCallback((value) => dispatch(actions.setLastName(value)), [dispatch]);
    const setPrimaryTitle = useCallback((value) => dispatch(actions.setPrimaryTitle(value)), [dispatch]);
    const setSecondaryTitle = useCallback((value) => dispatch(actions.setSecondaryTitle(value)), [dispatch]);
    const setTitle4 = useCallback((value) => dispatch(actions.setTitle4(value)), [dispatch]);
    const setMain = useCallback((value) => dispatch(actions.setMain(value)), [dispatch]);
    const setDirect = useCallback((value) => dispatch(actions.setDirect(value)), [dispatch]);
    const setWebsite = useCallback((value) => dispatch(actions.setWebsite(value)), [dispatch]);
    const setSchedulingCard = useCallback((value) => dispatch(actions.setSchedulingCard(value)), [dispatch]);
    const addTitle = useCallback(() => dispatch(actions.addTitle()), [dispatch]);
    const removeTitle = useCallback((count) => dispatch(actions.removeTitle(count)), [dispatch]);
    const setPersonsNames = useCallback((value) => dispatch(actions.setPersonsNames(value)), [dispatch]);
    const setPersonsTitles = useCallback((value) => dispatch(actions.setPersonsTitles(value)), [dispatch]);
    const addPerson = useCallback(() => dispatch(actions.addPerson()), [dispatch]);
    const removePerson = useCallback((count) => dispatch(actions.removePerson(count)), [dispatch]);
    const resetState = useCallback(() => dispatch(actions.resetState()), [dispatch]);
    const addToCart = useCallback((orderType, history, type) => dispatch(actions.addToCart(orderType, history, type)), [dispatch]);
    const addFavorite = useCallback((history) => dispatch(actions.addFavorite(history)), [dispatch]);
    const checkFavorite = useCallback((templateCode) => dispatch(actions.checkFavorite(templateCode)), [dispatch]);
    const setAction = useCallback((value) => dispatch(actions.setAction(value)), [dispatch]);
    const loadVariables = useCallback(() => dispatch(actions.loadVariables()), [dispatch]);
    const setAddToCartOrderType = useCallback((value) => dispatch(actions.setAddToCartOrderType(value)), [dispatch]);
    const setAddToCartType = useCallback((value) => dispatch(actions.setAddToCartType(value)), [dispatch]);
    const setPassword = useCallback((value) => dispatch(actions.setPassword(value)), [dispatch]);
    const setShowChecks = useCallback((value) => dispatch(actions.setShowChecks(value)), [dispatch]);
    const setAddEnvelopes = useCallback((value) => dispatch(actions.setAddEnvelopes(value)), [dispatch]);
    const fetchCheckControl = useCallback(() => dispatch(actions.fetchCheckControl()), [dispatch]);
    const addLetterheadPerson = useCallback(() => dispatch(actions.addLetterheadPerson()), [dispatch]);
    const removeLetterheadPerson = useCallback(() => dispatch(actions.removeLetterheadPerson()), [dispatch]);
    const setLetterheadPersonName = useCallback((index, value) => dispatch(actions.setLetterheadPersonName(index, value)), [dispatch]);
    const addLetterheadPersonTitle = useCallback((index) => dispatch(actions.addLetterheadPersonTitle(index)), [dispatch]);
    const removeLetterheadPersonTitle = useCallback((index) => dispatch(actions.removeLetterheadPersonTitle(index)), [dispatch]);
    const setLetterheadPersonTitle = useCallback((personIndex, titleIndex, value) => dispatch(actions.setLetterheadPersonTitle(personIndex, titleIndex, value)), [dispatch]);
    const addPrescriptionPadPerson = useCallback(() => dispatch(actions.addPrescriptionPadPerson()), [dispatch]);
    const removePrescriptionPadPerson = useCallback(() => dispatch(actions.removePrescriptionPadPerson()), [dispatch]);
    const removePrescriptionPadPersonByIndex = useCallback((index) => dispatch(actions.removePrescriptionPadPersonByIndex(index)), [dispatch]);
    const setPrescriptionPadPersonField = useCallback((index, field, value) => dispatch(actions.setPrescriptionPadPersonField(index, field, value)), [dispatch]);
    const setWindow = useCallback((value) => dispatch(actions.setWindow(value)), [dispatch]);
    const setShowSocialMediaIcons = useCallback((value) => dispatch(actions.setShowSocialMediaIcons(value)), [dispatch]);
    const setPhoneLabel1 = useCallback((value) => dispatch(actions.setPhoneLabel1(value)), [dispatch]);
    const setPhoneLabel2 = useCallback((value) => dispatch(actions.setPhoneLabel2(value)), [dispatch]);
    const setPhoneLabel3 = useCallback((value) => dispatch(actions.setPhoneLabel3(value)), [dispatch]);
    const setPhoneOtherLabel1 = useCallback((value) => dispatch(actions.setPhoneOtherLabel1(value)), [dispatch]);
    const setPhoneOtherLabel2 = useCallback((value) => dispatch(actions.setPhoneOtherLabel2(value)), [dispatch]);
    const setPhoneOtherLabel3 = useCallback((value) => dispatch(actions.setPhoneOtherLabel3(value)), [dispatch]);
    const setAdditionalField = useCallback((value) => dispatch(actions.setAdditionalField(value)), [dispatch]);
    const setLanguage = useCallback((value) => dispatch(actions.setLanguage(value)), [dispatch]);
    const fetchClinics = useCallback(() => dispatch(actions.fetchClinics()), [dispatch]);
    const fetchClinicLocations = useCallback((id) => dispatch(actions.fetchClinicLocations(id)), [dispatch]);
    const setClinicId = useCallback((value) => dispatch(actions.setClinicId(value)), [dispatch]);
    const setLocationId = useCallback((value) => dispatch(actions.setLocationId(value)), [dispatch]);
    const checkAvailableItems = useCallback((clinicId, templateCode, language) => dispatch(actions.checkAvailableItems(clinicId, templateCode, language)), [dispatch]);
    const showConfirmationModal = useCallback(() => dispatch(dialog.actions.showDialog('confirmation-modal')), [dispatch]);
    const hideConfirmationModal = useCallback(() => dispatch(dialog.actions.hideDialog('confirmation-modal')), [dispatch]);

    const parsedTemplateCode = 'p' + props.match.params.productId + 't' + props.match.params.templateId;
    /* componentDidMount */
    useEffect(() => {
        const templateCode = 'p' + props.match.params.productId + 't' + props.match.params.templateId;
        setTemplateCode(templateCode);
        checkFavorite(templateCode);

        if (props.match.params.action === 'edit') {
            loadVariables();
            setAction('edit');
        } else if (props.match.params.action === 'reorder') {
            loadVariables();
            setAction('reorder');
        }

        if (templateCode === 'p1t12') {
            fetchCheckControl();
        } else if (templateCode === 'p22t1' || templateCode === 'p22t2' || templateCode === 'p22t3') {
            setLanguage('English');
            fetchClinics();
        } else if (templateCode === 'p9t13') {
            setPhoneLabel2('Cell');
        }

        if (props.match.params.action !== 'edit') {
            if (templateCode === 'p9t1') {
                setSuiteRoom('6901 Bertner Avenue');
                setAddress2('Houston, Texas 77030');
            } else if (templateCode === 'p9t2') {
                setSuiteRoom('6431 Fannin Street');
                setAddress2('Houston, Texas 77030');
            } else if (templateCode === 'p9t3') {
                setSuiteRoom('6767 Bertner Avenue');
                setAddress2('Houston, Texas 77030');
            } else if (templateCode === 'p9t4') {
                setSuiteRoom('7000 Fannin Street');
                setAddress2('Houston, Texas 77030');
            } else if (templateCode === 'p9t5') {
                setSuiteRoom('7500 Cambridge Street');
                setAddress2('Houston, Texas 77054');
            } else if (templateCode === 'p9t9') {
                setSuiteRoom('6410 Fannin Street, Suite 100');
                setAddress2('Houston, Texas 77030');
            } else if (templateCode === 'p9t11') {
                setSuiteRoom('7000 Fannin Street');
                setAddress2('Houston, Texas 77030');
            } else if (templateCode === 'p9t12') {
                setSuiteRoom('1200 Pressler Street');
                setAddress2('Houston, Texas 77030');
            } else if (templateCode === 'p11t1') {
                setSuiteRoom('6901 Bertner Avenue');
                setAddress2('Houston, Texas 77030');
            } else if (templateCode === 'p11t2') {
                setSuiteRoom('6431 Fannin Street');
                setAddress2('Houston, Texas 77030');
            } else if (templateCode === 'p11t3') {
                setSuiteRoom('6767 Bertner Avenue');
                setAddress2('Houston, Texas 77030');
            } else if (templateCode === 'p11t4') {
                setSuiteRoom('7500 Cambridge Street');
                setAddress2('Houston, Texas 77054');
            } else if (templateCode === 'p11t22') {
                setSuiteRoom('7000 Fannin Street');
                setAddress2('Houston, Texas 77030');
            } else if (templateCode === 'p11t23') {
                setSuiteRoom('7000 Fannin Street');
                setAddress2('Houston, Texas 77030');
            } else if (templateCode === 'p11t24') {
                setSuiteRoom('1941 East Road');
                setAddress2('Houston, Texas 77054');
            } else if (templateCode === 'p11t25') {
                setSuiteRoom('1941 East Road');
                setAddress2('Houston, Texas 77054');
            } else if (templateCode === 'p12t2') {
                setAddress('7500 Cambridge Street');
                setAddress2('Houston, Texas 77054');
            } else if (templateCode === 'p12t3') {
                setAddress('6901 Bertner Avenue');
                setAddress2('Houston, Texas 77030');
            } else if (templateCode === 'p12t5') {
                setAddress('6431 Fannin Street');
                setAddress2('Houston, Texas 77030');
            } else if (templateCode === 'p12t5') {
                setAddress('6767 Bertner Avenue');
                setAddress2('Houston, Texas 77030');
            } else if (templateCode === 'p12t8') {
                setAddress('7000 Fannin Street, 6th Floor');
                setAddress2('Houston, Texas 77030');
            } else if (templateCode === 'p12t9') {
                setAddress('1200 Pressler Street');
                setAddress2('Houston, Texas 77030');
            } else if (templateCode === 'p12t10') {
                setAddress('1941 East Road');
                setAddress2('Houston, Texas 77054');
            } else if (templateCode === 'p20t7') {
                setPhoneLabel1('Phone');
                setPhoneLabel2('Cell');
            } else if (templateCode === 'p20t8') {
                setPhoneLabel1('Phone');
                setPhoneLabel2('Cell');
            } else if (templateCode === 'p20t9') {
                setPhoneLabel1('Phone');
                setPhoneLabel2('Cell');
            } else if (['p1t13', 'p1t14', 'p1t15', 'p1t16', 'p2t3', 'p3t4', 'p3t5', 'p3t6', 'p18t3', 'p21t1', 'p21t2'].includes(templateCode)) {
                setState('TX');
            }
        }

        return () => {
            resetState();
            changeFrontAll('');
            changeBackAll('');
            changePrevFrontAll('');
            changePrevBackAll('');
            changePrevLogoAll('');
            setTemplateCodeCanvas('');
        };
    }, [setTemplateCode, resetState, checkFavorite, loadVariables, setAction, fetchCheckControl, setSuiteRoom, setAddress, setAddress2, setState,
        setPhoneLabel1, setPhoneLabel2, setLanguage, fetchClinics, props.match.params.productId, props.match.params.templateId, 
        props.match.params.action]);

    useEffect(() => {
        setUser(user);
    }, [user]);

    let editorContainerWidth = 985;
    let leftContainerBasis = 370;
    let rightContainerBasis = 580;
    if (['p3t1', 'p3t3', 'p3t4', 'p3t6', 'p11t1', 'p11t2', 'p11t3', 'p11t4', 'p11t5', 'p11t6', 'p11t7', 'p21t1', 'p21t2', 'p11t22', 'p11t23', 'p11t25'].includes(parsedTemplateCode)) {
        editorContainerWidth = 1120;
        leftContainerBasis = 320;
        rightContainerBasis = 800;
    } else if (['p11t24'].includes(parsedTemplateCode)) {
        editorContainerWidth = 1100;
        leftContainerBasis = 350;
        rightContainerBasis = 750;
    } else if (['p10t1', 'p10t2', 'p10t3', 'p10t4', 'p10t5', 'p10t6', 'p10t7', 'p10t8', 'p10t9', 'p10t10'].includes(parsedTemplateCode)) {
        editorContainerWidth = 1100;
        leftContainerBasis = 350;
        rightContainerBasis = 750;
    } else if (parsedTemplateCode === 'p3t2') {
        editorContainerWidth = 1000;
        leftContainerBasis = 350;
        rightContainerBasis = 750;
    } else if (parsedTemplateCode === 'p3t5') {
        editorContainerWidth = 1000;
        leftContainerBasis = 350;
        rightContainerBasis = 750;
    } else if (parsedTemplateCode === 'p10t7') {
        editorContainerWidth = 995;
        leftContainerBasis = 380;
        rightContainerBasis = 580;
    } else if (['p11t8', 'p11t9', 'p11t10', 'p11t11', 'p11t12', 'p11t13', 'p11t14'].includes(parsedTemplateCode)) {
        editorContainerWidth = 1100;
        leftContainerBasis = 370;
        rightContainerBasis = 780;
    } else if (['p18t2'].includes(parsedTemplateCode)) {
        editorContainerWidth = 1100;
        leftContainerBasis = 450;
        rightContainerBasis = 650;
    } else if (['p18t3'].includes(parsedTemplateCode)) {
        editorContainerWidth = 995;
        leftContainerBasis = 380;
        rightContainerBasis = 580;
    } else if (['p19t1'].includes(parsedTemplateCode)) {
        editorContainerWidth = 985;
        leftContainerBasis = 480;
        rightContainerBasis = 470;
    } else if (['p12t1', 'p12t2', 'p12t3', 'p12t4', 'p12t5', 'p12t6', 'p12t7', 'p12t8', 'p12t9', 'p12t10'].includes(parsedTemplateCode)) {
        editorContainerWidth = 985;
        leftContainerBasis = 420;
        rightContainerBasis = 530;
    } else if (['p22t1', 'p22t2', 'p22t3'].includes(parsedTemplateCode)) {
        editorContainerWidth = 1000;
        leftContainerBasis = 495;
        rightContainerBasis = 470;
    }

    let breadcrumbsContainer = null;
    if (parseInt(props.match.params.productId) === 1) {
        let templateName = '';
        if (parseInt(props.match.params.templateId) === 8) {
            templateName = 'Clinic Appointment Card Reminder';
        } else if (parseInt(props.match.params.templateId) === 9) {
            templateName = 'Business Card';
        } else if (parseInt(props.match.params.templateId) === 10) {
            templateName = 'Billing Card';
        } else if (parseInt(props.match.params.templateId) === 11) {
            templateName = 'Provider Appointment Card Reminder';
        } else if (parseInt(props.match.params.templateId) === 12) {
            templateName = 'Customer Treasury Checks';
        } else if (parseInt(props.match.params.templateId) === 13) {
            templateName = 'Clinic Location Card';
        } else if (parseInt(props.match.params.templateId) === 14) {
            templateName = 'Clinic Appointment Reminder Card';
        } else if (parseInt(props.match.params.templateId) === 16) {
            templateName = 'Business Card';
        } else if (parseInt(props.match.params.templateId) === 17) {
            templateName = 'Billing Card';
        } else if (parseInt(props.match.params.templateId) === 15) {
            templateName = 'Provider Appointment Reminder Card';
        } else if (parseInt(props.match.params.templateId) === 18) {
            templateName = 'ENT Online Scheduling Cards';
        }

        const breadcrumbs = [
            <Link to="/utp">Main Categories</Link>,
            ' / ',
            <Link to="/product/1">Cards</Link>,
            ' / ',
            templateName,
        ];
        breadcrumbsContainer = (
            <div className="breadcrumbs">/ {breadcrumbs}</div>
        );
    } else if (parseInt(props.match.params.productId) === 2) {
        let templateName = '';
        if (parseInt(props.match.params.templateId) === 1) {
            templateName = 'Custom Variable Letterhead';
        } else if (parseInt(props.match.params.templateId) === 2) {
            templateName = 'Electronic Letterhead Download';
        } else if (parseInt(props.match.params.templateId) === 3) {
            templateName = 'Custom Letterhead';
        } else if (parseInt(props.match.params.templateId) === 4) {
            templateName = 'Electronic Letterhead Download';
        }

        const breadcrumbs = [
            <Link to="/utp">Main Categories</Link>,
            ' / ',
            <Link to="/product/2">Stationery Letterheads</Link>,
            ' / ',
            templateName,
        ];
        breadcrumbsContainer = (
            <div className="breadcrumbs">/ {breadcrumbs}</div>
        );
    } else if (parseInt(props.match.params.productId) === 3) {
        let templateName = '';
        if (parseInt(props.match.params.templateId) === 1) {
            templateName = 'No. 10 Envelope';
        } else if (parseInt(props.match.params.templateId) === 2) {
            templateName = 'A6 Envelope';
        } else if (parseInt(props.match.params.templateId) === 3) {
            templateName = '#10 Window Envelope';
        } else if (parseInt(props.match.params.templateId) === 4) {
            templateName = 'No. 10 Envelope';
        } else if (parseInt(props.match.params.templateId) === 5) {
            templateName = 'A6 Envelope';
        } else if (parseInt(props.match.params.templateId) === 6) {
            templateName = 'No. 10 Window Envelope';
        }

        const breadcrumbs = [
            <Link to="/utp">Main Categories</Link>,
            ' / ',
            <Link to="/product/3">Stationery Envelopes</Link>,
            ' / ',
            templateName,
        ];
        breadcrumbsContainer = (
            <div className="breadcrumbs">/ {breadcrumbs}</div>
        );
    } else if (parseInt(props.match.params.productId) === 4) {
        let templateName = '';
        if (parseInt(props.match.params.templateId) === 1) {
            templateName = '#A6 Foldover Card';
        } else if (parseInt(props.match.params.templateId) === 2) {
            templateName = 'Multipurpose Thank You Note Card';
        } else if (parseInt(props.match.params.templateId) === 3) {
            templateName = 'A6 Folded Notecard';
        }

        const breadcrumbs = [
            <Link to="/utp">Main Categories</Link>,
            ' / ',
            <Link to="/product/4">Notecards</Link>,
            ' / ',
            templateName,
        ];
        breadcrumbsContainer = (
            <div className="breadcrumbs">/ {breadcrumbs}</div>
        );
    } else if (parseInt(props.match.params.productId) === 9) {
        let templateName = '';
        if (parseInt(props.match.params.templateId) === 1) {
            templateName = 'UTHealth Houston | Cizik School of Nursing Business Card';
        } else if (parseInt(props.match.params.templateId) === 2) {
            templateName = 'UTHealth Houston | McGovern Medical School Business Card';
        } else if (parseInt(props.match.params.templateId) === 3) {
            templateName = 'MD Anderson Cancer Center | UTHealth Houston Graduate School Business Card';
        } else if (parseInt(props.match.params.templateId) === 4) {
            templateName = 'UTHealth Houston | School of Biomedical Informatics Business Card';
        } else if (parseInt(props.match.params.templateId) === 5) {
            templateName = 'UTHealth Houston | School of Dentistry Business Card';
        } else if (parseInt(props.match.params.templateId) === 6) {
            templateName = 'UTHealth | School of Public Health Business Card';
        } else if (parseInt(props.match.params.templateId) === 7) {
            templateName = 'UTHealth Houston General Card Business Card';
        } else if (parseInt(props.match.params.templateId) === 8) {
            templateName = 'UTHealth Houston with Social Media Handles Business Card';
        } else if (parseInt(props.match.params.templateId) === 9) {
            templateName = 'UTHealth Houston | UTHealth Services Business Card';
        } else if (parseInt(props.match.params.templateId) === 10) {
            templateName = 'UTHealth Houston | UTHealth Behavioral Sciences Business Card';
        } else if (parseInt(props.match.params.templateId) === 11) {
            templateName = 'UTHealth Houston | McWilliams School of Biomedical Informatics Business Card';
        } else if (parseInt(props.match.params.templateId) === 12) {
            templateName = 'UTHealth Houston | School of Public Health Business Card';
        } else if (parseInt(props.match.params.templateId) === 13) {
            templateName = 'UTHealth Houston | UTHealth Behavioral Sciences Business Card';
        } else if (parseInt(props.match.params.templateId) === 14) {
            templateName = 'UTHealth Houston General Card Business Card';
        } else if (parseInt(props.match.params.templateId) === 15) {
            templateName = 'UTHealth Houston with Social Media Handles Business Card';
        } else if (parseInt(props.match.params.templateId) === 16) {
            templateName = 'UTHealth Houston Executive Business Card';
        } else if (parseInt(props.match.params.templateId) === 17) {
            templateName = 'UTHealth Houston | Cizik School of Nursing Business Card';
        } else if (parseInt(props.match.params.templateId) === 18) {
            templateName = 'UTHealth Houston | McGovern Medical School Business Card';
        } else if (parseInt(props.match.params.templateId) === 19) {
            templateName = 'UTHealth Houston | McWilliams School of Biomedical Informatics Business Card';
        } else if (parseInt(props.match.params.templateId) === 20) {
            templateName = 'UTHealth Houston | School of Dentistry Business Card';
        } else if (parseInt(props.match.params.templateId) === 21) {
            templateName = 'UTHealth Houston | School of Public Health Business Card';
        } else if (parseInt(props.match.params.templateId) === 22) {
            templateName = 'UTHealth Houston | UTHealth Services Business Card';
        } else if (parseInt(props.match.params.templateId) === 23) {
            templateName = 'MD Anderson Cancer Center | UTHealth Houston Graduate School Business Card';
        }

        const breadcrumbs = [
            <Link to="/uth">Main Categories</Link>,
            ' / ',
            <Link to="/product/9">Business Cards</Link>,
            ' / ',
            templateName,
        ];
        breadcrumbsContainer = (
            <div className="breadcrumbs">/ {breadcrumbs}</div>
        );
    } else if (parseInt(props.match.params.productId) === 10) {
        let templateName = '';
        if (parseInt(props.match.params.templateId) === 1) {
            templateName = 'UTHealth Houston | Cizik School of Nursing Letterhead';
        } else if (parseInt(props.match.params.templateId) === 2) {
            templateName = 'UTHealth Houston | McGovern Medical School Letterhead';
        } else if (parseInt(props.match.params.templateId) === 3) {
            templateName = 'MD Anderson Cancer Center | UTHealth Houston Graduate School Letterhead';
        } else if (parseInt(props.match.params.templateId) === 4) {
            templateName = 'UTHealth Houston | School of Biomedical Informatics Letterhead';
        } else if (parseInt(props.match.params.templateId) === 5) {
            templateName = 'UTHealth Houston | School of Dentistry Letterhead';
        } else if (parseInt(props.match.params.templateId) === 6) {
            templateName = 'UTHealth | School of Public Health Letterhead';
        } else if (parseInt(props.match.params.templateId) === 7) {
            templateName = 'UTHealth Houston | General Letterhead';
        } else if (parseInt(props.match.params.templateId) === 8) {
            templateName = 'UTHealth Houston | McWilliams School of Biomedical Informatics Letterhead';
        } else if (parseInt(props.match.params.templateId) === 9) {
            templateName = 'UTHealth Houston | School of Public Health Letterhead';
        } else if (parseInt(props.match.params.templateId) === 9) {
            templateName = 'UTHealth Houston | UTHealth Behavioral Sciences Letterhead';
        }

        const breadcrumbs = [
            <Link to="/uth">Main Categories</Link>,
            ' / ',
            <Link to="/product/10">Letterheads</Link>,
            ' / ',
            templateName,
        ];
        breadcrumbsContainer = (
            <div className="breadcrumbs">/ {breadcrumbs}</div>
        );
    } else if (parseInt(props.match.params.productId) === 11) {
        let templateName = '';
        if (parseInt(props.match.params.templateId) === 1) {
            templateName = 'UTHealth Houston | Cizik School of Nursing #10 Envelopes';
        } else if (parseInt(props.match.params.templateId) === 2) {
            templateName = 'UTHealth Houston | McGovern Medical School #10 Envelopes';
        } else if (parseInt(props.match.params.templateId) === 3) {
            templateName = 'MD Anderson Cancer Center | UTHealth Houston Graduate School #10 Envelopes';
        } else if (parseInt(props.match.params.templateId) === 4) {
            templateName = 'UTHealth Houston | School of Dentistry #10 Envelopes';
        } else if (parseInt(props.match.params.templateId) === 5) {
            templateName = 'UTHealth | School of Public Health #10 Envelopes';
        } else if (parseInt(props.match.params.templateId) === 6) {
            templateName = 'UTHealth Houston | School of Biomedical Informatics #10 Envelopes';
        } else if (parseInt(props.match.params.templateId) === 7) {
            templateName = 'UTHealth Houston | General #10 Envelopes';
        } else if (parseInt(props.match.params.templateId) === 8) {
            templateName = 'MD Anderson Cancer Center UTHealth Graduate School A10 Envelope';
        } else if (parseInt(props.match.params.templateId) === 9) {
            templateName = 'UTHealth Cizik School of Nursing A10 Envelope';
        } else if (parseInt(props.match.params.templateId) === 10) {
            templateName = 'UTHealth General Administration A10 Envelope';
        } else if (parseInt(props.match.params.templateId) === 11) {
            templateName = 'UTHealth McGovern Medical School A10 Envelope';
        } else if (parseInt(props.match.params.templateId) === 12) {
            templateName = 'UTHealth School of Biomedical Informatics A10 Envelope';
        } else if (parseInt(props.match.params.templateId) === 13) {
            templateName = 'UTHealth School of Dentistry A10 Envelope';
        } else if (parseInt(props.match.params.templateId) === 14) {
            templateName = 'UTHealth School of Public Health A10 Envelope';
        } else if (parseInt(props.match.params.templateId) === 15) {
            templateName = 'Cizik School of Nursing A6 Envelope';
        } else if (parseInt(props.match.params.templateId) === 16) {
            templateName = 'McGovern Medical School A6 Envelope';
        } else if (parseInt(props.match.params.templateId) === 17) {
            templateName = 'MD Anderson Cancer Center UTHealth Graduate School A6 Envelope';
        } else if (parseInt(props.match.params.templateId) === 18) {
            templateName = 'School of Biomedical Informatics A6 Envelope';
        } else if (parseInt(props.match.params.templateId) === 19) {
            templateName = 'School of Dentistry A6 Envelope';
        } else if (parseInt(props.match.params.templateId) === 20) {
            templateName = 'School of Public Health A6 Envelope';
        } else if (parseInt(props.match.params.templateId) === 21) {
            templateName = 'UTHealth General Administration A6 Envelope';
        } else if (parseInt(props.match.params.templateId) === 22) {
            templateName = 'UTHealth Houston | McWilliams School of Biomedical Informatics #10 Envelopes';
        } else if (parseInt(props.match.params.templateId) === 23) {
            templateName = 'UTHealth Houston | School of Public Health #10 Envelopes';
        } else if (parseInt(props.match.params.templateId) === 24) {
            templateName = 'UTHealth Houston | UTHealth Behavioral Sciences Business A6 Envelopes';
        } else if (parseInt(props.match.params.templateId) === 25) {
            templateName = 'UTHealth Houston | UTHealth Behavioral Sciences #10 Envelopes';
        }

        const breadcrumbs = [
            <Link to="/uth">Main Categories</Link>,
            ' / ',
            <Link to="/product/11">Envelopes</Link>,
            ' / ',
            templateName,
        ];
        breadcrumbsContainer = (
            <div className="breadcrumbs">/ {breadcrumbs}</div>
        );
    } else if (parseInt(props.match.params.productId) === 12) {
        let templateName = '';
        if (parseInt(props.match.params.templateId) === 1) {
            templateName = 'UTHealth Houston | School of Biomedical Informatics Thank You Cards and Envelopes';
        } else if (parseInt(props.match.params.templateId) === 2) {
            templateName = 'UTHealth Houston | School of Dentistry Thank You Cards and Envelopes';
        } else if (parseInt(props.match.params.templateId) === 3) {
            templateName = 'UTHealth Houston | Cizik School of Nursing Thank You Cards and Envelopes';
        } else if (parseInt(props.match.params.templateId) === 4) {
            templateName = 'UTHealth | School of Public Health Thank You Cards and Envelopes';
        } else if (parseInt(props.match.params.templateId) === 5) {
            templateName = 'UTHealth Houston | McGovern Medical School Thank You Cards and Envelopes';
        } else if (parseInt(props.match.params.templateId) === 6) {
            templateName = 'MD Anderson Cancer Center | UTHealth Houston Graduate School Foldover Thank You Cards and Envelopes';
        } else if (parseInt(props.match.params.templateId) === 7) {
            templateName = 'UTHealth Houston | General Thank You Cards and Envelopes';
        } else if (parseInt(props.match.params.templateId) === 8) {
            templateName = 'UTHealth Houston | McWilliams School of Biomedical Informatics Thank You Cards and Envelopes';
        } else if (parseInt(props.match.params.templateId) === 9) {
            templateName = 'UTHealth Houston | School of Public Health Thank You Cards and Envelopes';
        } else if (parseInt(props.match.params.templateId) === 10) {
            templateName = 'UTHealth Houston | UTHealth Behavioral Sciences Thank You Cards and Envelopes';
        }

        const breadcrumbs = [
            <Link to="/uth">Main Categories</Link>,
            ' / ',
            <Link to="/product/12">Thank You Cards and Envelopes</Link>,
            ' / ',
            templateName,
        ];
        breadcrumbsContainer = (
            <div className="breadcrumbs">/ {breadcrumbs}</div>
        );
    } else if (parseInt(props.match.params.productId) === 17) {
        let templateName = '';
        if (parseInt(props.match.params.templateId) === 1) {
            templateName = 'OB-GYN | Women’s Care Centers Folder';
        } else if (parseInt(props.match.params.templateId) === 2) {
            templateName = 'Orthopedic Surgery Folder';
        } else if (parseInt(props.match.params.templateId) === 3) {
            templateName = 'Co-branded General Use Folder';
        } else if (parseInt(props.match.params.templateId) === 4) {
            templateName = 'Cardiothoracic & Vascular Surgery';
        } else if (parseInt(props.match.params.templateId) === 5) {
            templateName = 'Urology Folder';
        }

        const breadcrumbs = [
            <Link to="/uth">Main Categories</Link>,
            ' / ',
            <Link to="/product/17">Pocket Folders</Link>,
            ' / ',
            templateName,
        ];
        breadcrumbsContainer = (
            <div className="breadcrumbs">/ {breadcrumbs}</div>
        );
    } else if (parseInt(props.match.params.productId) === 18) {
        let templateName = '';
        if (parseInt(props.match.params.templateId) === 1) {
            templateName = 'VATS and Lobectomy Surgery Notepad';
        } else if (parseInt(props.match.params.templateId) === 2) {
            templateName = 'Emergency/Downtime Prescription Pad';
        } else if (parseInt(props.match.params.templateId) === 3) {
            templateName = 'Clinic Notepad';
        } else if (parseInt(props.match.params.templateId) === 4) {
            templateName = 'Newborn Hearing Screening (NBHS) Results Card';
        }

        const breadcrumbs = [
            <Link to="/uth">Main Categories</Link>,
            ' / ',
            <Link to="/product/18">Notepads</Link>,
            ' / ',
            templateName,
        ];
        breadcrumbsContainer = (
            <div className="breadcrumbs">/ {breadcrumbs}</div>
        );
    } else if (parseInt(props.match.params.productId) === 20) {
        let templateName = '';
        if (parseInt(props.match.params.templateId) === 1) {
            templateName = 'UTHealth Houston | Cizik School of Nursing Pocket Card';
        } else if (parseInt(props.match.params.templateId) === 2) {
            templateName = 'UTHealth Houston | McGovern Medical School Pocket Card';
        } else if (parseInt(props.match.params.templateId) === 3) {
            templateName = 'UTHealth Houston | School of Biomedical Informatics Pocket Card';
        } else if (parseInt(props.match.params.templateId) === 4) {
            templateName = 'UTHealth Houston | School of Dentistry Pocket Card';
        } else if (parseInt(props.match.params.templateId) === 5) {
            templateName = 'UTHealth | School of Public Health Pocket Card';
        } else if (parseInt(props.match.params.templateId) === 6) {
            templateName = 'UTHealth Houston | General Pocket Card';
        } else if (parseInt(props.match.params.templateId) === 7) {
            templateName = 'UTHealth Houston | McWilliams School of Biomedical Informatics Pocket Card';
        } else if (parseInt(props.match.params.templateId) === 8) {
            templateName = 'UTHealth Houston | School of Public Health Pocket Card';
        } else if (parseInt(props.match.params.templateId) === 9) {
            templateName = 'UTHealth Houston | UTHealth Behavioral Sciences Pocket Card';
        }

        const breadcrumbs = [
            <Link to="/uth">Main Categories</Link>,
            ' / ',
            <Link to="/product/20">Pocket Cards</Link>,
            ' / ',
            templateName,
        ];
        breadcrumbsContainer = (
            <div className="breadcrumbs">/ {breadcrumbs}</div>
        );
    } else if (parseInt(props.match.params.productId) === 21) {
        let templateName = '';
        if (parseInt(props.match.params.templateId) === 1) {
            templateName = '9x12" Catalog Envelope';
        } else if (parseInt(props.match.params.templateId) === 2) {
            templateName = '10x13" Catalog Envelope';
        }

        const breadcrumbs = [
            <Link to="/utp">Main Categories</Link>,
            ' / ',
            <Link to="/product/21">Catalog Envelopes</Link>,
            ' / ',
            templateName,
        ];
        breadcrumbsContainer = (
            <div className="breadcrumbs">/ {breadcrumbs}</div>
        );
    } else if (parseInt(props.match.params.productId) === 22) {
        let templateName = '';
        if (parseInt(props.match.params.templateId) === 1) {
            templateName = 'Travel Notice Vinyl Wall Cling';
        } else if (parseInt(props.match.params.templateId) === 2) {
            templateName = 'CDC Cover Your Cough Vinyl Wall Cling';
        } else if (parseInt(props.match.params.templateId) === 3) {
            templateName = 'No Recording Notice Vinyl Wall Cling';
        }

        const breadcrumbs = [
            <Link to="/utp">Main Categories</Link>,
            ' / ',
            <Link to="/product/22">Clinical Signage</Link>,
            ' / ',
            templateName,
        ];
        breadcrumbsContainer = (
            <div className="breadcrumbs">/ {breadcrumbs}</div>
        );
    }

    let content = <div className="text-center"><i className="fas fa-circle-notch fa-spin" /> Loading data...</div>;
    if (!checkingFavorite) {
        content = (
            <Fragment>
                <div className="fonts-helper">
                    <div className="AGaramondProItalic">a</div>
                    <div className="AGaramondProRegular">a</div>
                    <div className="Ariblk">a</div>
                    <div className="AGaramondProSemibold">a</div>
                    <div className="Roboto">a</div>
                    <div className="RobotoCondensed">a</div>
                    <div className="UniversLTStd">a</div>
                    <div className="UniversLTStdBlack">a</div>
                    <div className="AGaramondProSemiboldItalic">a</div>
                    <div className="EBGaramond">a</div>
                    <div className="GaramondPremrPro">a</div>
                    <div className="GaramondPremrProSmbd">a</div>
                    <div className="UniversBold">a</div>
                    <div className="UniversLT49LightUltraCondensed">a</div>
                    <div className="UniversLTStdObl">a</div>
                    <div className="LinotypeUniversLTStdLight">a</div>
                    <div className="AGaramondProBold">a</div>
                </div>
                <div className="steps-content">
                    <div className="left-side-container" style={{ flexBasis: leftContainerBasis }}>
                        <Form
                            user={user}
                            name={name}
                            setName={setName}
                            credentials={credentials}
                            setCredentials={setCredentials}
                            title1={title1}
                            setTitle1={setTitle1}
                            title2={title2}
                            setTitle2={setTitle2}
                            title3={title3}
                            setTitle3={setTitle3}
                            department1={department1}
                            setDepartment1={setDepartment1}
                            department2={department2}
                            setDepartment2={setDepartment2}
                            department3={department3}
                            setDepartment3={setDepartment3}
                            phone={phone}
                            setPhone={setPhone}
                            clinic={clinic}
                            setClinic={setClinic}
                            fax={fax}
                            setFax={setFax}
                            address={address}
                            setAddress={setAddress}
                            address2={address2}
                            setAddress2={setAddress2}
                            address3={address3}
                            setAddress3={setAddress3}
                            city={city}
                            setCity={setCity}
                            state={state}
                            setState={setState}
                            zip={zip}
                            setZip={setZip}
                            email={email}
                            setEmail={setEmail}
                            open1={open1}
                            setOpen1={setOpen1}
                            open2={open2}
                            setOpen2={setOpen2}
                            open3={open3}
                            setOpen3={setOpen3}
                            quantity={quantity}
                            setQuantity={setQuantity}
                            setFrontDataUrl={setFrontDataUrl}
                            setBackDataUrl={setBackDataUrl}
                            showEmail={showEmail}
                            setShowEmail={setShowEmail}
                            pager={pager}
                            setPager={setPager}
                            nursePhone={nursePhone}
                            setNursePhone={setNursePhone}
                            fontType={fontType}
                            setFontType={setFontType}
                            templateCode={templateCode}
                            cell={cell}
                            setCell={setCell}
                            showCell={showCell}
                            setShowCell={setShowCell}
                            locationNames={locationNames}
                            setLocationNames={setLocationNames}
                            locationPhones={locationPhones}
                            setLocationPhones={setLocationPhones}
                            locationFaxes={locationFaxes}
                            setLocationFaxes={setLocationFaxes}
                            locationNursePhones={locationNursePhones}
                            setLocationNursePhones={setLocationNursePhones}
                            locationAddresses1={locationAddresses1}
                            setLocationAddresses1={setLocationAddresses1}
                            locationAddresses2={locationAddresses2}
                            setLocationAddresses2={setLocationAddresses2}
                            locationAddresses3={locationAddresses3}
                            setLocationAddresses3={setLocationAddresses3}
                            locationSuites={locationSuites}
                            setLocationSuites={setLocationSuites}
                            locationCities={locationCities}
                            setLocationCities={setLocationCities}
                            locationStates={locationStates}
                            setLocationStates={setLocationStates}
                            locationZips={locationZips}
                            setLocationZips={setLocationZips}
                            color={color}
                            setColor={setColor}
                            stock={stock}
                            setStock={setStock}
                            titles={titles}
                            setTitles={setTitles}
                            addTitle={addTitle}
                            removeTitle={removeTitle}
                            personsNames={personsNames}
                            setPersonsNames={setPersonsNames}
                            addPerson={addPerson}
                            removePerson={removePerson}
                            personsTitles={personsTitles}
                            setPersonsTitles={setPersonsTitles}
                            notes={notes}
                            setNotes={setNotes}
                            contact1Phone={contact1Phone}
                            setContact1Phone={setContact1Phone}
                            contact1Type={contact1Type}
                            setContact1Type={setContact1Type}
                            contact2Phone={contact2Phone}
                            setContact2Phone={setContact2Phone}
                            contact2Type={contact2Type}
                            setContact2Type={setContact2Type}
                            history={props.history}
                            addingToCart={addingToCart}
                            addToCart={addToCart}
                            locations={locations}
                            setLocations={setLocations}
                            cxmlData={cxmlData}
                            isFavorite={isFavorite}
                            addFavorite={addFavorite}
                            checkingFavorite={checkingFavorite}
                            savingWork={savingWork}
                            action={action}
                            quantity1={quantity1}
                            quantity2={quantity2}
                            quantity3={quantity3}
                            fetchingCheckControl={fetchingCheckControl}
                            checkControl={checkControl}
                            front={front}
                            back={back}
                            suiteRoom={suiteRoom}
                            firstName={firstName}
                            middleName={middleName}
                            lastName={lastName}
                            primaryTitle={primaryTitle}
                            secondaryTitle={secondaryTitle}
                            title4={title4}
                            main={main}
                            direct={direct}
                            website={website}
                            addEnvelopes={addEnvelopes}
                            letterheadPersons={letterheadPersons}
                            prescriptionPadDoctors={prescriptionPadDoctors}
                            window={window}
                            showSocialMediaIcons={showSocialMediaIcons}
                            additionalField={additionalField}
                            schedulingCard={schedulingCard}
                            phoneLabel1={phoneLabel1}
                            phoneLabel2={phoneLabel2}
                            phoneLabel3={phoneLabel3}
                            phoneOtherLabel1={phoneOtherLabel1}
                            phoneOtherLabel2={phoneOtherLabel2}
                            phoneOtherLabel3={phoneOtherLabel3}
                            language={language}
                            clinicId={clinicId}
                            locationId={locationId}
                            clinics={clinics}
                            clinicLocations={clinicLocations}
                            fetchingClinics={fetchingClinics}
                            fetchingClinicLocations={fetchingClinicLocations}
                            availableItems={availableItems}
                            fetchingAvailableItems={fetchingAvailableItems}
                            setQuantity1={setQuantity1}
                            setQuantity2={setQuantity2}
                            setQuantity3={setQuantity3}
                            showConfirmationModal={showConfirmationModal}
                            setAddToCartOrderType={setAddToCartOrderType}
                            setAddToCartType={setAddToCartType}
                            setFront={setFront}
                            setBack={setBack}
                            setSuiteRoom={setSuiteRoom}
                            setFirstName={setFirstName}
                            setMiddleName={setMiddleName}
                            setLastName={setLastName}
                            setPrimaryTitle={setPrimaryTitle}
                            setSecondaryTitle={setSecondaryTitle}
                            setTitle4={setTitle4}
                            setMain={setMain}
                            setDirect={setDirect}
                            setWebsite={setWebsite}
                            setAddEnvelopes={setAddEnvelopes}
                            addLetterheadPerson={addLetterheadPerson}
                            removeLetterheadPerson={removeLetterheadPerson}
                            setLetterheadPersonName={setLetterheadPersonName}
                            addLetterheadPersonTitle={addLetterheadPersonTitle}
                            removeLetterheadPersonTitle={removeLetterheadPersonTitle}
                            setLetterheadPersonTitle={setLetterheadPersonTitle}
                            addPrescriptionPadPerson={addPrescriptionPadPerson}
                            removePrescriptionPadPerson={removePrescriptionPadPerson}
                            removePrescriptionPadPersonByIndex={removePrescriptionPadPersonByIndex}
                            setPrescriptionPadPersonField={setPrescriptionPadPersonField}
                            setWindow={setWindow}
                            setShowSocialMediaIcons={setShowSocialMediaIcons}
                            setAdditionalField={setAdditionalField}
                            setSchedulingCard={setSchedulingCard}
                            setPhoneLabel1={setPhoneLabel1}
                            setPhoneLabel2={setPhoneLabel2}
                            setPhoneLabel3={setPhoneLabel3}
                            setPhoneOtherLabel1={setPhoneOtherLabel1}
                            setPhoneOtherLabel2={setPhoneOtherLabel2}
                            setPhoneOtherLabel3={setPhoneOtherLabel3}
                            setLanguage={setLanguage}
                            fetchClinics={fetchClinics}
                            fetchClinicLocations={fetchClinicLocations}
                            setClinicId={setClinicId}
                            setLocationId={setLocationId}
                            checkAvailableItems={checkAvailableItems}
                        />
                    </div>
                    <div className="right-side-container" style={{ flexBasis: rightContainerBasis }}>
                        <FrontCanvas
                            user={user}
                            templateCode={templateCode}
                            color={color}
                            templateId={parsedTemplateCode}
                            quantity1={quantity1}
                            quantity2={quantity2}
                            quantity3={quantity3}
                            front={front}
                            addEnvelopes={addEnvelopes}
                            schedulingCard={schedulingCard}
                            language={language}
                        />
                        <BackCanvas
                            templateCode={templateCode}
                            templateId={parsedTemplateCode}
                        />
                    </div>
                </div>
            </Fragment>
        );
    }

    if (templateCode === 'p1t12' && !showChecks) {
        const validatePassword = () => {
            if (password !== 'bq2SUM') {
                return showNotification('Wrong Password', 'The password is incorrect', 'info');
            }

            setShowChecks(true);
        };

        const handleChangePassword = (event) => {
            setPassword(event.target.value);
        };

        content = (
            <div className="password-container">
                <div><b>Password</b></div>
                <input type="password" placeholder="Enter the password" value={password} onChange={handleChangePassword} />
                <button onClick={validatePassword}>Enter</button>
            </div>
        );
    }

    return (
        <Layout
            title={<Fragment><i className="fas fa-paint-brush"></i> Editor</Fragment>}
            step={3}
            history={props.history}
        >
            <Helmet><title>Editor - UT Physicians</title></Helmet>
            <div className="editor-container" style={{ width: editorContainerWidth }}>
                {breadcrumbsContainer}
                {content}
            </div>
            <ConfirmationModal
                history={props.history}
                dialogState={dialogState}
                addToCartOrderType={addToCartOrderType}
                addToCartType={addToCartType}
                templateCode={templateCode}
                hideConfirmationModal={hideConfirmationModal}
                setAddToCartOrderType={setAddToCartOrderType}
                setAddToCartType={setAddToCartType}
                addToCart={addToCart}
            />
        </Layout>
    );
};

Editor.propTypes = {

};

export default Editor;
